// hooks/useServerData.js — Read/write data from Worker KV via API
// Provides a useState-like API but syncs with the server.
// Falls back to localStorage when not authenticated.

import { useState, useEffect, useRef, useCallback } from 'react';
import { getSessionToken } from '../services/authApi';

import { API_BASE } from '../config';
const DEBOUNCE_MS = 500;

async function kvGet(scope, key) {
  const token = getSessionToken();
  if (!token) return null;
  const res = await fetch(`${API_BASE}/api/data/${scope}/${key}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const msg = `KV GET ${scope}/${key} failed (${res.status})`;
    console.warn('[useServerData]', msg);
    throw new Error(msg);
  }
  return res.json();
}

async function kvPut(scope, key, value) {
  const token = getSessionToken();
  if (!token) return;
  const res = await fetch(`${API_BASE}/api/data/${scope}/${key}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(value),
  });
  if (!res.ok) {
    const msg = `KV PUT ${scope}/${key} failed (${res.status})`;
    console.warn('[useServerData]', msg);
    throw new Error(msg);
  }
}

/**
 * useServerData — like useState, but synced with Worker KV.
 *
 * @param {string} key       - KV key (e.g. 'watchlist', 'portfolio_funds')
 * @param {string} scope     - 'personal' or 'shared'
 * @param {*} defaultValue   - fallback if KV returns null
 * @param {string} lsKey     - optional localStorage key for fallback/migration
 * @returns {[value, setValue, { loading, synced, syncError }]}
 */
export function useServerData(key, scope, defaultValue, lsKey = null) {
  // Initialize from localStorage for instant display
  const [value, setValueRaw] = useState(() => {
    if (lsKey) {
      try {
        const raw = localStorage.getItem(lsKey);
        return raw ? JSON.parse(raw) : defaultValue;
      } catch { return defaultValue; }
    }
    return defaultValue;
  });
  const [loading, setLoading] = useState(true);
  const [synced, setSynced] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const debounceRef = useRef(null);
  const mountedRef = useRef(true);
  // Ref that always holds the latest value (for beforeunload closure)
  const valueRef = useRef(value);
  useEffect(() => { valueRef.current = value; }, [value]);

  // Fetch from KV on mount
  useEffect(() => {
    mountedRef.current = true;
    const token = getSessionToken();
    if (!token) {
      setLoading(false);
      return;
    }

    kvGet(scope, key)
      .then((data) => {
        if (!mountedRef.current) return;
        if (data !== null) {
          setValueRaw(data);
          setSynced(true);
          setSyncError(null);
        }
        // If KV is empty but we have localStorage data → first-time sync
        else if (lsKey && value !== defaultValue) {
          kvPut(scope, key, value).catch((err) => {
            if (mountedRef.current) setSyncError(err.message);
          });
          setSynced(true);
        }
        // KV empty and no localStorage to sync → reset to default
        // Critical for scope changes: prevents stale data from previous scope
        else {
          setValueRaw(defaultValue);
        }
      })
      .catch((err) => {
        console.error('[useServerData] fetch error:', err.message);
        if (mountedRef.current) setSyncError(err.message);
      })
      .finally(() => { if (mountedRef.current) setLoading(false); });

    return () => { mountedRef.current = false; };
  }, [scope, key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced save to KV
  // opts.immediate = true bypasses debounce (e.g. clearing chat on new report)
  const setValue = useCallback((newValueOrFn, opts = {}) => {
    setValueRaw((prev) => {
      const next = typeof newValueOrFn === 'function' ? newValueOrFn(prev) : newValueOrFn;

      // Also save to localStorage as backup
      if (lsKey) {
        try { localStorage.setItem(lsKey, JSON.stringify(next)); } catch {}
      }

      // Debounce KV write (or flush immediately if opts.immediate)
      clearTimeout(debounceRef.current);
      const delay = opts.immediate ? 0 : DEBOUNCE_MS;
      debounceRef.current = setTimeout(() => {
        kvPut(scope, key, next)
          .then(() => { setSyncError(null); })
          .catch((err) => {
            console.error('[useServerData] sync error:', err.message);
            setSyncError(err.message);
          });
      }, delay);

      return next;
    });
  }, [scope, key, lsKey]);

  // Flush on beforeunload — B1 fix: token in body, not URL; B7 fix: use valueRef for fresh value
  useEffect(() => {
    const handleUnload = () => {
      clearTimeout(debounceRef.current);
      const token = getSessionToken();
      if (!token) return;
      // sendBeacon cannot set custom headers, so we embed auth token in the JSON body.
      // The Worker extracts it from body._token when Authorization header is absent.
      const payload = { _token: token, _data: valueRef.current };
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(`${API_BASE}/api/data/${scope}/${key}`, blob);
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [scope, key]);

  return [value, setValue, { loading, synced, syncError }];
}
